import { useRef } from 'react'
import { motion, useInView } from 'motion/react'
import { colors, accent } from '@/lib/tokens'

const ease = [0.16, 1, 0.3, 1] as const

const PACKAGES = [
  { name: '@recost/sdk', lang: 'Node / TypeScript', install: 'npm install @recost/sdk' },
  { name: 'recost', lang: 'Python', install: 'pip install recost' },
  { name: 'recost.vsix', lang: 'VS Code extension', install: 'Install from marketplace' },
]

const STATS = [
  { value: 'MIT', label: 'License' },
  { value: 'Node + Python', label: 'SDKs' },
  { value: 'D1 + Workers', label: 'Backend' },
]

function GitHubIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" />
    </svg>
  )
}

export function OpenSourceSection() {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true, margin: '-80px' })
  const pkgRef = useRef<HTMLDivElement>(null)
  const pkgInView = useInView(pkgRef, { once: true, margin: '-60px' })

  return (
    <section
      className="oss-section"
      style={{
        backgroundColor: colors.bgPage,
        padding: '100px 20px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div className="glow-orb-teal" style={{ width: 600, height: 600, bottom: -200, right: '5%', opacity: 0.7 }} />
      <div className="glow-orb" style={{ width: 500, height: 500, top: -100, left: '8%' }} />

      <div style={{ maxWidth: 1280, margin: '0 auto', position: 'relative', zIndex: 1 }}>
        {/* Top: headline + body + CTAs */}
        <div
          ref={ref}
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 64,
            alignItems: 'start',
            marginBottom: 72,
          }}
          className="oss-grid"
        >
          {/* Left: copy */}
          <div>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, ease }}
              style={{ fontSize: 12, fontWeight: 500, letterSpacing: '0.06em', textTransform: 'uppercase' as const, color: accent, marginBottom: 16 }}
            >
              Open source Extension
            </motion.p>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: 0.06, ease }}
              style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 700, letterSpacing: '-0.02em', color: colors.textPrimary, lineHeight: 1.1, margin: '0 0 20px' }}
            >
              Built in the open.{' '}
              <span style={{ color: accent }}>For developers.</span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: 0.1, ease }}
              style={{ fontSize: 16, lineHeight: 1.65, color: colors.textSecondary, margin: '0 0 32px' }}
            >
              Recost VS-Code Extension is MIT-licensed and fully open source. Read every line,
              fork it, contribute fixes, and use our online dashboard for free analytics. No black boxes. No lock-in.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: 0.14, ease }}
              style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}
            >
              <a
                href="https://github.com/recost-dev/extension"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  fontSize: 14, fontWeight: 600, color: colors.bgBase,
                  textDecoration: 'none', padding: '11px 20px', borderRadius: 8, background: colors.textPrimary,
                  transition: 'opacity 0.15s', letterSpacing: '-0.01em',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.82')}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
              >
                <GitHubIcon />
                View on GitHub
              </a>
              <a
                href="/docs"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  fontSize: 14, fontWeight: 500, color: colors.textSecondary,
                  textDecoration: 'none', padding: '11px 18px', borderRadius: 8,
                  border: `1px solid ${colors.borderDefault}`, background: colors.bgBase,
                  transition: 'border-color 0.15s, color 0.15s', letterSpacing: '-0.01em',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = colors.borderHover; e.currentTarget.style.color = colors.textPrimary }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = colors.borderDefault; e.currentTarget.style.color = colors.textSecondary }}
              >
                Read the docs
              </a>
            </motion.div>

            {/* Stats row */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: 0.2, ease }}
              style={{ display: 'flex', gap: 28, flexWrap: 'wrap', marginTop: 36 }}
            >
              {STATS.map((stat) => (
                <div key={stat.label} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary, letterSpacing: '-0.01em' }}>{stat.value}</span>
                  <span style={{ fontSize: 11, color: colors.textMuted }}>{stat.label}</span>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right: package list */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.45, delay: 0.18, ease }}
            style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
          >
            {PACKAGES.map((pkg, i) => (
              <div
                key={pkg.name}
                style={{
                  background: colors.bgBase,
                  border: `1px solid ${colors.borderDefault}`,
                  borderRadius: 10,
                  padding: '18px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: colors.textPrimary, marginBottom: 2 }}>{pkg.name}</div>
                    <div style={{ fontSize: 12, color: colors.textMuted }}>{pkg.lang}</div>
                  </div>
                  <div
                    style={{
                      width: 28, height: 28, borderRadius: 6,
                      background: i === 0 ? colors.textPrimary : i === 1 ? '#3b82f6' : colors.accentSubtle,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    {i === 0 && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                        <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" />
                      </svg>
                    )}
                    {i === 1 && (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="white">
                        <path d="M14.25.18l.9.2.73.26.59.3.45.32.34.34.25.34.16.33.1.3.04.26.02.2-.01.13V8.5l-.05.63-.13.55-.21.46-.26.38-.3.31-.33.25-.35.19-.35.14-.33.1-.3.07-.26.04-.21.02H8.77l-.69.05-.59.14-.5.22-.41.27-.33.32-.27.35-.2.36-.15.37-.1.35-.07.32-.04.27-.02.21v3.06H3.17l-.21-.03-.28-.07-.32-.12-.35-.18-.36-.26-.36-.36-.35-.46-.32-.59-.28-.73-.21-.88-.14-1.05-.05-1.23.06-1.22.16-1.04.24-.87.32-.71.36-.57.4-.44.42-.33.42-.24.4-.16.36-.1.32-.05.24-.01h.16l.06.01h8.16v-.83H6.18l-.01-2.75-.02-.37.05-.34.11-.31.17-.28.25-.26.31-.23.38-.2.44-.18.51-.15.58-.12.64-.1.71-.06.77-.04.84-.02 1.27.05 1.07.13zm-6.3 1.98l-.23.33-.08.41.08.41.23.34.33.22.41.09.41-.09.33-.22.23-.34.08-.41-.08-.41-.23-.33-.33-.22-.41-.09-.41.09-.33.22zM21.1 6.11l.28.06.32.12.35.18.36.27.36.35.35.47.32.59.28.73.21.88.14 1.04.05 1.23-.06 1.23-.16 1.04-.24.86-.32.71-.36.57-.4.45-.42.33-.42.24-.4.16-.36.09-.32.05-.24.02-.16-.01h-8.22v.82h5.84l.01 2.76.02.36-.05.34-.11.31-.17.29-.25.25-.31.24-.38.2-.44.17-.51.15-.58.13-.64.09-.71.07-.77.04-.84.01-1.27-.04-1.07-.14-.9-.2-.73-.25-.59-.3-.45-.33-.34-.34-.25-.34-.16-.33-.1-.3-.04-.25-.02-.2.01-.13v-5.34l.05-.64.13-.54.21-.46.26-.38.3-.31.33-.25.35-.19.35-.14.33-.1.3-.06.26-.04.21-.02.13-.01h5.84l.69-.05.59-.14.5-.21.41-.28.33-.32.27-.35.2-.36.15-.36.1-.35.07-.32.04-.28.02-.21V6.07h2.09l.14.01.21.03zm-6.47 14.25l-.23.33-.08.41.08.41.23.33.33.23.41.08.41-.08.33-.23.23-.33.08-.41-.08-.41-.23-.33-.33-.23-.41-.08-.41.08-.33.23z" />
                      </svg>
                    )}
                    {i === 2 && (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#F26B1D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                      </svg>
                    )}
                  </div>
                </div>
                <div
                  style={{
                    background: colors.bgSubtle,
                    borderRadius: 6,
                    padding: '8px 12px',
                    fontFamily: 'var(--font-mono)',
                    fontSize: 12,
                    color: colors.textPrimary,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <span style={{ color: colors.textMuted }}>$</span>
                  {pkg.install}
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Bottom: contribution callout */}
        <div ref={pkgRef}>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={pkgInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.45, ease }}
            style={{
              background: colors.bgBase,
              border: `1px solid ${colors.borderDefault}`,
              borderRadius: 12,
              padding: '36px 40px',
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              alignItems: 'center',
              gap: 32,
            }}
            className="contribute-grid"
          >
            <div>
              <h3 style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em', color: colors.textPrimary, margin: '0 0 10px' }}>
                Want to contribute?
              </h3>
              <p style={{ fontSize: 14, lineHeight: 1.65, color: colors.textSecondary, margin: 0, maxWidth: 500 }}>
                Recost is actively developed and welcomes contributions. Whether it's a new provider integration, a bug fix, or a dashboard improvement — open a PR and we'll review it fast.
              </p>
            </div>
            <a
              href="https://github.com/recost-dev/extension/issues"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                fontSize: 14, fontWeight: 600, color: colors.textPrimary,
                textDecoration: 'none', padding: '11px 20px', borderRadius: 8,
                border: `1px solid ${colors.borderDefault}`, background: colors.bgPage, whiteSpace: 'nowrap',
                transition: 'border-color 0.15s, background 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = colors.borderHover; e.currentTarget.style.background = colors.bgBase }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = colors.borderDefault; e.currentTarget.style.background = colors.bgPage }}
            >
              <GitHubIcon />
              Open an issue or PR
            </a>
          </motion.div>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .oss-section { padding: 60px 20px !important; }
          .oss-grid { grid-template-columns: 1fr !important; gap: 40px !important; margin-bottom: 40px !important; }
          .contribute-grid { grid-template-columns: 1fr !important; padding: 24px 20px !important; gap: 20px !important; }
        }
      `}</style>
    </section>
  )
}
